import { useEffect, useState, type ChangeEvent } from "react";

import type { PricingConfig } from "@/lib/types";
import { DEFAULT_PRICING } from "@/lib/pricing";

interface Props {
  value: PricingConfig;
  onChange: (next: PricingConfig) => void;
}

/**
 * Inline editor for `Project.pricing`. Debounced (250ms) so dragging a
 * range slider doesn't hammer ddb.
 */
export function PricingEditor({ value, onChange }: Props) {
  const [local, setLocal] = useState<PricingConfig>(value);
  useEffect(() => { setLocal(value); }, [value.laborPerCabinet, value.markup, value.taxRate, value.delivery]);

  // Debounced commit
  useEffect(() => {
    if (
      local.laborPerCabinet === value.laborPerCabinet &&
      local.markup === value.markup &&
      local.taxRate === value.taxRate &&
      local.delivery === value.delivery
    ) return;
    const t = setTimeout(() => onChange(local), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  return (
    <div className="card" style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <h4 style={{ margin: 0, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5 }}>Pricing</h4>
        <span className="muted" style={{ fontSize: 11 }}>cost-plus</span>
        <span style={{ flex: 1 }} />
        <button
          className="ghost"
          style={{ fontSize: 11, padding: "2px 8px" }}
          onClick={() => setLocal(DEFAULT_PRICING)}
          title="Reset all fields to default"
        >
          Reset
        </button>
      </div>

      <Field
        label="Labor / cabinet"
        prefix="$"
        value={local.laborPerCabinet}
        step={10}
        onChange={(v) => setLocal({ ...local, laborPerCabinet: v })}
      />
      <Field
        label="Markup"
        suffix="%"
        value={Math.round(local.markup * 1000) / 10}
        step={5}
        onChange={(v) => setLocal({ ...local, markup: v / 100 })}
      />
      <Field
        label="Sales tax"
        suffix="%"
        value={Math.round(local.taxRate * 1000) / 10}
        step={0.5}
        onChange={(v) => setLocal({ ...local, taxRate: v / 100 })}
      />
      <Field
        label="Delivery (flat)"
        prefix="$"
        value={local.delivery}
        step={25}
        onChange={(v) => setLocal({ ...local, delivery: v })}
      />
    </div>
  );
}

interface FieldProps {
  label: string;
  prefix?: string;
  suffix?: string;
  value: number;
  step: number;
  onChange: (v: number) => void;
}

function Field({ label, prefix, suffix, value, step, onChange }: FieldProps) {
  function handle(e: ChangeEvent<HTMLInputElement>) {
    const v = Number(e.target.value);
    if (Number.isFinite(v) && v >= 0) onChange(v);
  }
  return (
    <label style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 12 }}>{label}</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {prefix && <span className="muted" style={{ fontSize: 12 }}>{prefix}</span>}
        <input
          type="number"
          value={value}
          step={step}
          min={0}
          onChange={handle}
          style={{ width: 90, fontVariantNumeric: "tabular-nums" }}
        />
        {suffix && <span className="muted" style={{ fontSize: 12 }}>{suffix}</span>}
      </span>
    </label>
  );
}
